import React from 'react';

import ExplorerIcon from 'components/FileViewer/ExplorerIcon';
import formatBytes from 'helper/formatBytes';
import { ExplorerFile, pluralize } from 'helper/products/fileExplorer';
import { ZipProgress } from 'utils/hooks/file_viewer/useFileViewerDownload';

import './index.scss';

/**
 * O .zip é montado na memória da aba (não há rota de zip no backend). Acima disto o navegador
 * pode travar; o usuário baixa em partes.
 */
export const ZIP_MAX_BYTES = 500 * 1000 * 1000;

export default function SelectionBar({
  files,
  progress,
  onDownload,
  onCancel,
  onClear,
}: {
  files: ExplorerFile[];
  progress: ZipProgress | null;
  onDownload: () => void;
  onCancel: () => void;
  onClear: () => void;
}) {
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const tooLarge = files.length > 1 && totalBytes > ZIP_MAX_BYTES;
  const percent = progress ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="file-explorer-selection" role="region" aria-label="Seleção">
      <div className="file-explorer-selection-summary">
        <b>{pluralize(files.length, 'arquivo selecionado', 'arquivos selecionados')}</b>
        <span className="file-explorer-selection-size">{formatBytes(totalBytes)}</span>
        {tooLarge && (
          <span className="file-explorer-selection-warning">
            {`O .zip aceita até ${formatBytes(ZIP_MAX_BYTES)} — baixe em partes.`}
          </span>
        )}
      </div>

      {progress ? (
        <div className="file-explorer-selection-progress" role="status">
          <span>{`Preparando .zip — ${progress.done} de ${progress.total}`}</span>
          <span className="file-explorer-progress-track" aria-hidden="true">
            <span className="file-explorer-progress-fill" style={{ width: `${percent}%` }} />
          </span>
          <button type="button" className="file-explorer-selection-ghost" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      ) : (
        <div className="file-explorer-selection-actions">
          <button
            type="button"
            className="file-explorer-selection-primary"
            onClick={onDownload}
            disabled={tooLarge}
          >
            <ExplorerIcon name="download" size={15} strokeWidth={2.2} />
            {files.length === 1 ? 'Baixar selecionado' : 'Baixar selecionados (.zip)'}
          </button>
          <button type="button" className="file-explorer-selection-ghost" onClick={onClear}>
            Limpar
          </button>
        </div>
      )}
    </div>
  );
}
